<template>
  <section
    class="trending-now-widget"
    :class="{ 'trending-now-widget--compact': compact }"
  >
    <header class="trending-now-widget__header">
      <v-icon size="18" class="trending-now-widget__icon">mdi-fire</v-icon>
      <h2 class="trending-now-widget__title">Trending Now</h2>
    </header>

    <div v-if="isLoading" class="trending-now-widget__loading">
      <v-progress-circular indeterminate size="24" color="primary" />
    </div>

    <div v-else class="trending-now-widget__content">
      <!-- Trending Hashtags -->
      <div
        v-if="trendingHashtags.length > 0"
        class="trending-now-widget__section"
      >
        <span class="trending-now-widget__label">
          <v-icon size="14">mdi-pound</v-icon> Hashtags
        </span>
        <button
          v-for="hashtag in trendingHashtags"
          :key="hashtag.hashtag"
          class="trending-now-widget__item"
          @click="navigateToHashtag(hashtag.hashtag)"
        >
          <div class="trending-now-widget__item-content">
            <span class="trending-now-widget__tag">{{ hashtag.hashtag }}</span>
            <span class="trending-now-widget__count"
              >{{ formatCount(hashtag.count) }} posts today</span
            >
          </div>
          <v-icon size="16" class="trending-now-widget__arrow"
            >mdi-chevron-right</v-icon
          >
        </button>
      </div>

      <!-- Trending Songs -->
      <div v-if="trendingSongs.length > 0" class="trending-now-widget__section">
        <span class="trending-now-widget__label">
          <v-icon size="14">mdi-music</v-icon> Songs
        </span>
        <button
          v-for="song in trendingSongs"
          :key="song.ulid"
          class="trending-now-widget__item"
          @click="navigateToSong(song.ulid)"
        >
          <div class="trending-now-widget__item-artwork">
            <v-img v-if="song.coverImage" :src="song.coverImage" cover />
            <v-icon v-else size="16">mdi-music-note</v-icon>
          </div>
          <div class="trending-now-widget__item-content">
            <span class="trending-now-widget__tag">{{
              song.title || "No Title"
            }}</span>
            <span class="trending-now-widget__count">{{ song.artist }}</span>
          </div>
          <v-icon size="16" class="trending-now-widget__arrow">mdi-play</v-icon>
        </button>
      </div>

      <!-- Trending Video Creators -->
      <div
        v-if="trendingVideoCreators.length > 0"
        class="trending-now-widget__section"
      >
        <span class="trending-now-widget__label">
          <v-icon size="14">mdi-video</v-icon> Videos
        </span>
        <button
          v-for="creator in trendingVideoCreators"
          :key="creator.ulid"
          class="trending-now-widget__item trending-now-widget__item--creator"
          @click="navigateToCreator(creator.username)"
        >
          <div class="trending-now-widget__avatar">
            <v-img v-if="creator.avatar" :src="creator.avatar" cover />
            <span v-else>{{ creator.username.charAt(0).toUpperCase() }}</span>
          </div>
          <div class="trending-now-widget__item-content">
            <span class="trending-now-widget__tag">{{ creator.username }}</span>
            <span class="trending-now-widget__count"
              >{{ formatCount(creator.recentViews) }} plays</span
            >
          </div>
          <v-icon size="16" class="trending-now-widget__arrow"
            >mdi-chevron-right</v-icon
          >
        </button>
      </div>

      <!-- Trending Shorts Creators -->
      <div
        v-if="trendingShortsCreators.length > 0"
        class="trending-now-widget__section"
      >
        <span class="trending-now-widget__label">
          <v-icon size="14">mdi-play-box-multiple</v-icon> Shorts
        </span>
        <button
          v-for="creator in trendingShortsCreators"
          :key="creator.ulid"
          class="trending-now-widget__item trending-now-widget__item--creator"
          @click="navigateToCreator(creator.username)"
        >
          <div class="trending-now-widget__avatar">
            <v-img v-if="creator.avatar" :src="creator.avatar" cover />
            <span v-else>{{ creator.username.charAt(0).toUpperCase() }}</span>
          </div>
          <div class="trending-now-widget__item-content">
            <span class="trending-now-widget__tag">{{ creator.username }}</span>
            <span class="trending-now-widget__count"
              >{{ formatCount(creator.recentViews) }} plays</span
            >
          </div>
          <v-icon size="16" class="trending-now-widget__arrow"
            >mdi-chevron-right</v-icon
          >
        </button>
      </div>

      <div v-if="!hasTrendingContent" class="trending-now-widget__empty">
        <p>No trending content yet</p>
      </div>
    </div>

    <button
      v-if="showExploreMore && hasTrendingContent"
      class="trending-now-widget__explore"
      @click="router.push('/explore')"
    >
      Explore more
      <v-icon size="14">mdi-chevron-right</v-icon>
    </button>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuth } from "@/composables/useAuth";
import { useSdk } from "@/composables/useSdk";
import type { TrendingUser } from "@cc-consulting-nv/ccsdk";

interface Props {
  compact?: boolean;
  showExploreMore?: boolean;
}

withDefaults(defineProps<Props>(), {
  compact: false,
  showExploreMore: false,
});

interface TrendingHashtag {
  hashtag: string;
  count: number;
}

interface TrendingSong {
  ulid: string;
  title: string;
  artist: string;
  username?: string;
  coverImage?: string;
}

const router = useRouter();
const { isAuthenticated } = useAuth();
const { sdk, isInitialized } = useSdk();

const trendingHashtags = ref<TrendingHashtag[]>([]);
const trendingSongs = ref<TrendingSong[]>([]);
const trendingVideoCreators = ref<TrendingUser[]>([]);
const trendingShortsCreators = ref<TrendingUser[]>([]);
const isLoading = ref(false);

const hasTrendingContent = computed(
  () =>
    trendingHashtags.value.length > 0 ||
    trendingSongs.value.length > 0 ||
    trendingVideoCreators.value.length > 0 ||
    trendingShortsCreators.value.length > 0
);

function formatCount(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return String(count);
}

async function loadTrendingData() {
  if (!isInitialized.value || !sdk) return;
  isLoading.value = true;
  try {
    const [hashtags, songs, videoCreators, shortsCreators] = await Promise.all([
      isAuthenticated.value
        ? sdk.getTrendingHashtags(3).catch(() => [])
        : Promise.resolve([]),
      sdk.getTrendingSongs(3, true).catch(() => []),
      sdk.getTrendingUsers({ limit: 3, kind: "videos" }).catch(() => []),
      sdk.getTrendingUsers({ limit: 3, kind: "shorts" }).catch(() => []),
    ]);
    trendingHashtags.value = hashtags;
    trendingSongs.value = songs;
    trendingVideoCreators.value = videoCreators;
    trendingShortsCreators.value = shortsCreators;
  } catch {
    // ignore
  } finally {
    isLoading.value = false;
  }
}

function navigateToHashtag(hashtag: string) {
  router.push(`/search?q=%23${encodeURIComponent(hashtag.replace(/^#/, ""))}`);
}

function navigateToSong(ulid: string) {
  router.push(`/song/${ulid}`);
}

function navigateToCreator(username: string) {
  router.push(`/profile/${username}`);
}

onMounted(() => loadTrendingData());
watch(isAuthenticated, () => {
  if (isAuthenticated.value) loadTrendingData();
  else trendingHashtags.value = [];
});
</script>

<style scoped lang="scss">
.trending-now-widget {
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  overflow: hidden;

  &--compact {
    border-radius: 12px;

    .trending-now-widget__header {
      padding: 12px 14px;
    }

    .trending-now-widget__content {
      padding: 6px;
    }

    .trending-now-widget__item {
      padding: 8px 10px;
    }
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }

  &__icon {
    color: rgb(var(--v-theme-primary));
  }

  &__title {
    font-family: var(--font-display);
    font-size: 1.125rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: rgb(var(--v-theme-on-surface));
    margin: 0;
  }

  &__loading,
  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;

    p {
      font-size: 0.8125rem;
      color: rgb(var(--v-theme-outline));
      margin: 0;
    }
  }

  &__content {
    padding: 8px;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
  }

  &__section {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px 4px;
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgb(var(--v-theme-outline));
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 10px 12px;
    border: none;
    background: none;
    border-radius: 12px;
    cursor: pointer;
    transition: background 0.2s ease;
    text-align: left;
    font-family: inherit;

    &:hover {
      background: rgba(255, 255, 255, 0.04);
    }
  }

  &__item-content {
    flex: 1;
    min-width: 0;
  }

  &__tag {
    display: block;
    font-weight: 600;
    font-size: 0.8125rem;
    color: rgb(var(--v-theme-on-surface));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__count {
    display: block;
    font-size: 0.75rem;
    color: rgb(var(--v-theme-outline));
    margin-top: 2px;
  }

  &__arrow {
    color: rgb(var(--v-theme-outline));
    flex-shrink: 0;
  }

  &__item-artwork {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    overflow: hidden;
    background: rgba(var(--v-theme-primary), 0.1);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    .v-icon {
      color: rgb(var(--v-theme-primary));
    }
  }

  &__avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    overflow: hidden;
    background: rgba(var(--v-theme-primary), 0.1);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    font-weight: 600;
    color: rgb(var(--v-theme-primary));
  }

  &__explore {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 100%;
    padding: 10px;
    border: none;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    background: none;
    font-family: var(--font-body);
    font-size: 0.8125rem;
    font-weight: 500;
    color: rgb(var(--v-theme-primary));
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
      background: rgba(var(--v-theme-primary), 0.05);
    }
  }
}
</style>
